import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  GridLegacy as Grid,
  SxProps,
  Theme,
  Typography,
  Zoom,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { MyOrderModel, useGetMyBooking } from '@/logic/services/my-booking';

import React, { useEffect, useMemo, useState } from 'react';
import { useAuth, useDialog, useSnack } from '@/logic/state';

import { BookingTypes } from '@/constants/booking';
import { CreditCardAccordion } from '@/components/profile/credit-card-accrodion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Head from 'next/head';
import Link from 'next/link';
import MyWalletCollapse from '@/features/wallet/components/wallet-access/my-wallet-collapse';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { PROFILE_CARDS_TRANSITION_TIMEOUT } from '@/constants/profile';
import { TopUpStatus } from '@/features/wallet/services/types';
import { isWalletFeatureEnabled } from '@/constants/wallet';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useGetTopUpById } from '@/features/wallet/services/_api';
import { useInvalidateWallet } from '@/features/wallet/hooks/use-invalidate-wallet';
import { useMe } from '@/logic/services';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useWalletStore } from '@/features/wallet/store/wallet-store';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import ImageBoxFuture from '@/components/shared/image-box-future';
import MyBookingCard from '@/components/shared/my-booking-card';
import { ProfileCard } from '@/components/profile/profile-card';
import { ProfileFormCard } from '@/components/profile/profile-form-card';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';
import CashbackCollapse from '@/features/cashback/components/wallet-access/cashback-collapse';
import WalletCashbackCompleteProfileBadge from '@/features/wallet/components/wallet-access/wallet-cashback-complete-profile-badge';
import { UserType, useUserTypes } from '@/logic/hooks/use-user-types';

const Profile: NextPage = () => {
  const theme = useTheme();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const router = useRouter();
  const setDialog = useDialog(s => s.setDialog, shallow);

  const { userType } = useUserTypes();

  const [_user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const [wallet, setWallet] = useWalletStore(s => [s.wallet, s.setWallet], shallow);
  // const { refetch: refetchNotifications } = useNotifications();
  const setSnack = useSnack(s => s.setSnack, shallow);

  const { invalidateAllWallet } = useInvalidateWallet();

  const topUpIdQueryKey = 'topUpId';
  const topUpIdRegex = router.asPath.match(
    new RegExp(`[&?]${topUpIdQueryKey}=(.*)(&|$)`)
  );
  const topUpIdQueryValue = router.query[topUpIdQueryKey] || topUpIdRegex?.[1];

  useMemo(() => {
    invalidateAllWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching } = useMe(
    {},
    {
      enabled: !!_user?.token,
    }
  );

  const isGetTopUpByIdEnabled = Boolean(topUpIdQueryValue);

  const { isFetching: isTopUpFetching } = useGetTopUpById(
    {
      query: {
        topUpId: topUpIdQueryValue as string,
      },
    },
    {
      enabled: isGetTopUpByIdEnabled,
      onSuccess: ({ topUp }) => {
        if (topUp.status === TopUpStatus.done) {
          setSnack({
            isOpen: true,
            message: intl.formatMessage(
              {
                id: 'wallet.top_up.successful.payment',
              },
              {
                balance: wallet?.topUp?.amount,
              }
            ),
            type: 'success',
          });
        } else {
          setSnack({
            isOpen: true,
            message: 'booking.payment.fail',
            type: 'error',
          });
        }
      },
      onSettled: () => {
        setWallet(prevState => ({
          topUp: {
            amount: prevState?.topUp?.amount,
          },
        }));
      },
    }
  );

  const [isProfileForm, handleIsProfileForm] = useState<boolean>(false);

  const refetch = (): Promise<void> =>
    queryClient.invalidateQueries([{ url: 'auth/me' }]);

  // useEffect(() => {
  // if (_user?.isCompleteProfile) refetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [_user, data]);

  useEffect(() => {
    _user && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_user?.id]);

  const sx: Partial<SxProps<Theme>> = {
    backgroundColor: theme.palette.layout.bgSecondary,
    paddingTop: pxToRem(48),
    paddingBottom: pxToRem(32),
    paddingInline: pxToRem(44),
    borderRadius: pxToRem(12),
    margin: {
      mobile: 'auto',
      tablet: 'unset',
    },
  };

  const { data: myBookingData } = useGetMyBooking(
    {
      params: {
        type: BookingTypes.UPCOMING,
        limit: 50,
        page: 1,
      },
    },
    {
      enabled: !!_user?.token,
    }
  );

  const { data: historyData } = useGetMyBooking(
    {
      params: {
        type: BookingTypes.HISTORY,
        limit: 1,
        page: 1,
      },
    },
    {
      enabled: !!_user?.token,
    }
  );

  useEffect(() => {
    if (data && !isFetching) {
      const { language: _, ...restUserData } = data.user;
      setUser(prevState => ({
        ...prevState,
        ...restUserData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [isProfileForm]);

  const handleCompleteProfileClick = (): void => {
    setDialog({
      wrapper: true,
      isCompleteProfileForm: true,
      completeProfileFormPayload: {
        hideMaybeLaterButton: true,
      },
    });
  };

  const viewHistory = {
    color: theme.palette.primary.main,
    fontWeight: 400,
    fontSize: { tablet: pxToRem(18), mobile: '16px' },
  };
  const accordionBoxIcon = {
    width: { tablet: pxToRem(56), mobile: '40px' },
    height: { tablet: pxToRem(56), mobile: '40px' },

    borderRadius: '9px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    mr: { tablet: pxToRem(16), mobile: '16px' },
  };

  const accordion = {
    display: 'flex',
    py: { tablet: pxToRem(16), mobile: '14px' },
    px: { tablet: pxToRem(14), mobile: '14px' },
    background: theme.palette.common.white,
    boxShadow: theme.customShadows.cardShadow,
    borderRadius: '16px',
    '& .MuiAccordionSummary-content': {
      margin: `0 !important`,
      alignItems: 'center',
    },
    mt: { tablet: pxToRem(46), mobile: '32px' },
  };

  const accordionTitle = {
    color: theme.palette.grey[80],
    fontWeight: 700,
    fontSize: { tablet: pxToRem(20), mobile: '16px' },
  };

  if (isTopUpFetching) {
    return (
      <MovieLoaderWrapper
        innerBox={{
          width: '160px',
          height: '160px',
          [theme.breakpoints.down('laptop')]: {
            width: '100px',
            height: '100px',
          },
        }}
        outerBox={{
          width: '100%',
          height: {
            mobile: 'calc(100vh - 56px)',
            tablet: 'calc(100vh - 100px)',
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    );
  }

  return (
    _user && (
      <>
        <Head>
          <title>{intl.formatMessage({ id: 'profile' })}</title>
        </Head>
        <Box
          sx={{
            position: 'relative',
            overflow: 'auto',
            top: '-2px',
          }}
        >
          <Box
            sx={{
              backgroundImage: 'url(/icons/profile/profile-banner-2.jpg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'top',
              backgroundSize: 'cover',
              height: { mobile: '10vh', tablet: '30vh' },
              width: '100vw',
              maxWidth: '100%',
            }}
          ></Box>
          <Box
            sx={{
              px: {
                laptop: pxToRem(128),
                tablet: pxToRem(60),
                mobile: '18px',
              },
            }}
          >
            <Grid
              container
              columnSpacing={{
                mobile: 2,
                tablet: 5,
              }}
            >
              <Grid
                item
                mobile={12}
                tablet={4}
                sx={{
                  marginTop: {
                    mobile: '-6vh',
                    tablet: '-140px',
                  },
                }}
              >
                <Zoom in={!isProfileForm} timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
                  <Box
                    sx={{
                      ...sx,
                      display: !isProfileForm ? 'block' : 'none',
                    }}
                  >
                    <ProfileCard user={_user} close={() => handleIsProfileForm(true)} />
                  </Box>
                </Zoom>
                <Zoom in={isProfileForm} timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
                  <Box
                    sx={{
                      ...sx,
                      display: isProfileForm ? 'block' : 'none',
                    }}
                  >
                    <ProfileFormCard
                      user={_user}
                      close={() => {
                        handleIsProfileForm(false);
                        refetch();
                      }}
                      closeBtn={true}
                    />
                  </Box>
                </Zoom>
              </Grid>
              <Grid item mobile={12} tablet={8}>
                {userType === UserType.Verified ? (
                  <WalletCashbackCompleteProfileBadge
                    message={'cashback.wallet.badge.complete.profile.verified'}
                    handleClick={() => handleIsProfileForm(true)}
                  />
                ) : (
                  <> </>
                )}
                {userType === UserType.NonVerified ? (
                  <WalletCashbackCompleteProfileBadge
                    message={'cashback.wallet.badge.complete.profile.not.verified'}
                    handleClick={() => handleIsProfileForm(true)}
                  />
                ) : (
                  <> </>
                )}
                {userType === UserType.NonCompleted ? (
                  <WalletCashbackCompleteProfileBadge
                    message={'wallet.user_not_completed_profile.side_menu'}
                    handleClick={() => handleCompleteProfileClick()}
                  />
                ) : (
                  <> </>
                )}
                {_user.isCompleteProfile && (
                  <Zoom in timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
                    <Accordion
                      sx={{
                        maxWidth: '910px',
                        mb: {
                          tablet: `${pxToRem(44)} !important`,
                          mobile: '32px !important',
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{
                              color: theme.palette.secondary.main,
                              width: { tablet: pxToRem(24), mobile: '24px' },
                              height: { tablet: pxToRem(24), mobile: '24px' },
                              alignItems: 'center',
                            }}
                          />
                        }
                        sx={{
                          ...accordion,
                        }}
                      >
                        <Box
                          component='div'
                          sx={{
                            ...accordionBoxIcon,
                            backgroundColor: theme.palette.secondary.main,
                          }}
                        >
                          <ImageBoxFuture
                            img='/icons/my-booking/myBookingAccordionIcon.svg'
                            title={intl.formatMessage({
                              id: 'alt.my_booking_icon',
                            })}
                            objectFit='contain'
                            sx={{
                              width: { tablet: pxToRem(33), mobile: '40px' },
                              height: { tablet: pxToRem(33), mobile: '24px' },
                            }}
                          />
                        </Box>
                        <Typography component='h2' sx={accordionTitle}>
                          <FormattedMessage id='my_booking' />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          mt: { tablet: pxToRem(40), mobile: '24px' },
                          position: 'relative',
                        }}
                      >
                        {myBookingData?.data?.length > 0 ? (
                          <Box component='div'>
                            <Box
                              component='div'
                              sx={{
                                mb: { tablet: pxToRem(44), mobile: '24px' },
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                component='h3'
                                sx={{
                                  color: theme.palette.grey[80],
                                  fontWeight: 700,
                                  fontSize: { tablet: pxToRem(20), mobile: '18px' },
                                }}
                              >
                                <FormattedMessage id='my_booking.upcoming_bookings' />
                              </Typography>
                            </Box>

                            <Grid
                              container
                              columnSpacing={{
                                mobile: 1,
                                tablet: 2,
                              }}
                            >
                              {myBookingData?.data?.map((item: MyOrderModel, index) => (
                                <Grid
                                  item
                                  mobile={12}
                                  tablet={12}
                                  laptop={6}
                                  key={item.id}
                                >
                                  <MyBookingCard item={item} index={index} />
                                </Grid>
                              ))}
                            </Grid>

                            {historyData?.data?.length > 0 && (
                              <Link
                                href={{ pathname: `/user-area/view-history` }}
                                legacyBehavior
        passHref
                                prefetch={false}
                              >
                                <Typography
                                  component='a'
                                  sx={{
                                    ...viewHistory,
                                    [theme.breakpoints.up('tablet')]: {
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      fontSize: `${pxToRem(18)} !important`,
                                    },
                                    [theme.breakpoints.down('tablet')]: {
                                      position: 'static !important',
                                    },
                                  }}
                                >
                                  <FormattedMessage id='my_booking.view_history' />
                                </Typography>
                              </Link>
                            )}
                          </Box>
                        ) : (
                          <Box
                            component='div'
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              position: 'relative',
                              mt: { tablet: pxToRem(36), mobile: '24px' },
                            }}
                          >
                            <ImageBoxFuture
                              img='/icons/my-booking/emptyUpComing.svg'
                              title={intl.formatMessage({
                                id: 'alt.no_data_icon',
                              })}
                              objectFit='contain'
                              sx={{
                                mb: { tablet: pxToRem(32), mobile: '16px' },
                                width: { tablet: pxToRem(248), mobile: '190px' },
                                height: { tablet: pxToRem(176), mobile: '135px' },
                              }}
                            />

                            <Typography
                              component='p'
                              sx={{
                                fontSize: { tablet: pxToRem(24), mobile: '16px' },
                                textAlign: 'center',
                                color: theme.palette.grey[50],
                                maxWidth: '461px',
                                lineHeight: '150%',
                              }}
                            >
                              <FormattedMessage id='my_booking.no_booking' />
                            </Typography>

                            {historyData?.data?.length > 0 && (
                              <Link
                                href={{ pathname: `/user-area/view-history` }}
                                legacyBehavior
        passHref
                                prefetch={false}
                              >
                                <Typography
                                  component='a'
                                  sx={{
                                    ...viewHistory,

                                    [theme.breakpoints.up('tablet')]: {
                                      position: 'absolute',
                                      right: 0,
                                      top: 0,
                                    },
                                    [theme.breakpoints.down('tablet')]: {
                                      mr: 'auto',
                                      mt: '32px',
                                    },
                                  }}
                                >
                                  <FormattedMessage id='my_booking.view_history' />
                                </Typography>
                              </Link>
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Zoom>
                )}
                {_user.isCompleteProfile && (
                  <CreditCardAccordion
                    accordion={accordion}
                    accordionBoxIcon={accordionBoxIcon}
                    accordionTitle={accordionTitle}
                  />
                )}
                {isWalletFeatureEnabled && (
                  <MyWalletCollapse
                    accordionSx={accordion}
                    accordionTitleSx={accordionTitle}
                    accordionBoxIconSx={accordionBoxIcon}
                  />
                )}
                {userType !== UserType.NonCompleted ? (
                  <CashbackCollapse
                    accordionSx={accordion}
                    accordionTitleSx={accordionTitle}
                    accordionBoxIconSx={accordionBoxIcon}
                  />
                ) : (
                  <></>
                )}
                <Zoom in timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
                  <Box component='div'>
                    <Link
                      href={{ pathname: `/user-area/delete-account` }}
                      legacyBehavior
        passHref
                      prefetch={false}
                    >
                      <Box
                        component='a'
                        sx={{
                          alignItems: 'center',
                          maxWidth: '910px',
                          mb: {
                            tablet: `${pxToRem(44)} !important`,
                            mobile: '32px !important',
                          },
                          ...accordion,
                          mt: { tablet: pxToRem(112), mobile: '126px' },
                          cursor: 'pointer',
                          textDecoration: 'none',
                          listStyle: 'none',
                        }}
                      >
                        <Box
                          component='div'
                          sx={{
                            ...accordionBoxIcon,
                            backgroundColor: theme.palette.error.main,
                          }}
                        >
                          <ImageBoxFuture
                            img='/icons/profile/deleteAccount.svg'
                            title={intl.formatMessage({
                              id: 'global.delete_icon',
                            })}
                            objectFit='contain'
                            sx={{
                              width: { tablet: pxToRem(33), mobile: '40px' },
                              height: { tablet: pxToRem(33), mobile: '24px' },
                            }}
                          />
                        </Box>
                        <Typography
                          component='p'
                          sx={{
                            ...accordionTitle,
                          }}
                        >
                          <FormattedMessage id='delete_account' />
                        </Typography>
                      </Box>
                    </Link>
                  </Box>
                </Zoom>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </>
    )
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { locale, defaultLocale } = context;

  return {
    props: {
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default Profile;
